import { MessageType } from "odyssey-shared";

/**
 * The server's `travelOffer` and `travelState` (ADR-137 decisions 8 and 12, PROTOCOL.md 6.83.0) as the travel store
 * takes them. The text schema has already bounded every field when a message reaches here, so nothing is checked twice;
 * a place's ten-item list is named here once.
 */

/**
 * One place of an offer: its name and region (each possibly empty), its kind, its map's name (empty for this map), its
 * ground point in tiles (a whole number of sixteenths), its coin and Focus costs as the server computed them, whether
 * the character can pay now, and, when it cannot, the reason's code (`short`, `focus`, `outlaw`, or one a later server
 * names).
 */
export interface TravelPlace {
  readonly name: string;
  readonly region: string;
  readonly kind: "shrine" | "waypoint";
  readonly map: string;
  readonly x: number;
  readonly y: number;
  readonly coin: number;
  readonly focus: number;
  readonly affordable: boolean;
  readonly reason: string;
}

/**
 * A `travelOffer`: its serial, which a `travel` names back, where it starts and the source's name, and the places in
 * the server's order (nearest first).
 */
export interface TravelOffer {
  readonly serial: number;
  readonly source: "shrine" | "anywhere";
  readonly sourceName: string;
  readonly places: readonly TravelPlace[];
}

/**
 * A `travelState`: a channel started (with the tick it ends), finished, broken or refused (each with its reason's code).
 */
export type TravelState =
  | { readonly state: "channel"; readonly name: string; readonly endsTick: number }
  | { readonly state: "done"; readonly name: string }
  | { readonly state: "broken" | "refused"; readonly name: string; readonly reason: string };

/**
 * A travel frame as the connection hands it on, or null for any other message.
 */
export type TravelEvent = { readonly kind: "offer"; readonly offer: TravelOffer } | { readonly kind: "state"; readonly state: TravelState };

/**
 * Reads a `travelOffer` or a `travelState`.
 *
 * @param message A parsed server message.
 * @returns The travel event, or null for any other message.
 * @example
 * readTravel({ type: MessageType.TravelState, state: "done", name: "Old Shrine" });
 * // { kind: "state", state: { state: "done", name: "Old Shrine" } }
 */
export function readTravel(message: Readonly<Record<string, unknown>> & { readonly type: number }): TravelEvent | null {
  if (message.type === MessageType.TravelOffer) {
    const places = (message["places"] as readonly (readonly unknown[])[]).map(
      (place): TravelPlace => ({
        name: place[0] as string,
        region: place[1] as string,
        kind: place[2] as TravelPlace["kind"],
        map: place[3] as string,
        x: place[4] as number,
        y: place[5] as number,
        coin: place[6] as number,
        focus: place[7] as number,
        affordable: place[8] as boolean,
        reason: place[9] as string,
      }),
    );
    return { kind: "offer", offer: { serial: message["serial"] as number, source: message["source"] as TravelOffer["source"], sourceName: message["sourceName"] as string, places } };
  }
  if (message.type === MessageType.TravelState) {
    const state = message["state"] as TravelState["state"];
    const name = message["name"] as string;
    if (state === "channel") {
      return { kind: "state", state: { state, name, endsTick: message["endsTick"] as number } };
    }
    if (state === "done") {
      return { kind: "state", state: { state, name } };
    }
    return { kind: "state", state: { state, name, reason: message["reason"] as string } };
  }
  return null;
}
